import classNames from 'classnames';
import type { FunctionComponent, MouseEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { DynamicButton } from '@trello/dynamic-tokens';
import { intl } from '@trello/i18n';
import { StarIcon } from '@trello/nachos/icons/star';
import { StarredIcon } from '@trello/nachos/icons/starred';
import { Tooltip } from '@trello/nachos/tooltip';

import { Util } from 'app/scripts/lib/util';
import { useIsBoardHeaderAndMenuRedesignEnabled } from 'app/src/components/BoardHeader/useIsBoardHeaderAndMenuRedesignEnabled';
import { useBoardHeaderAddBoardStarMutation } from './BoardHeaderAddBoardStarMutation.generated';
import { useBoardHeaderRemoveBoardStarMutation } from './BoardHeaderRemoveBoardStarMutation.generated';
import { useBoardHeaderStarButtonFragment } from './BoardHeaderStarButtonFragment.generated';

import * as styles from './BoardHeaderStarButton.module.less';

interface Props {
  boardId: string;
  isClosed: boolean;
  isTemplate: boolean;
}

export const BoardHeaderStarButton: FunctionComponent<Props> = ({
  boardId,
  isClosed,
  isTemplate,
}) => {
  const memberId = useMemberId();
  const [pendingChange, setPendingChange] = useState<
    'none' | 'starring' | 'unstarring'
  >('none');

  const [removeBoardStar] = useBoardHeaderRemoveBoardStarMutation();
  const [addBoardStar] = useBoardHeaderAddBoardStarMutation();

  const { headerRedesignEnabled } = useIsBoardHeaderAndMenuRedesignEnabled();

  const { data: boardStarsData } = useBoardHeaderStarButtonFragment({
    from: { id: memberId },
  });

  const boardStars = useMemo(
    () => boardStarsData?.boardStars ?? [],
    [boardStarsData?.boardStars],
  );

  const boardStar = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    return boardStars.find((boardStar) => boardStar.idBoard === boardId);
  }, [boardId, boardStars]);

  useEffect(() => {
    if (
      (pendingChange === 'starring' && boardStar) ||
      (pendingChange === 'unstarring' && !boardStar)
    ) {
      setPendingChange('none');
    }
  }, [boardStar, pendingChange]);

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (boardStar) {
        setPendingChange('unstarring');
        removeBoardStar({
          variables: {
            boardStarId: boardStar.id,
            memberId,
          },
        });
      } else {
        setPendingChange('starring');
        addBoardStar({
          variables: {
            boardId,
            memberId,
            pos:
              Math.max(0, ...boardStars.map(({ pos }) => pos)) + Util.spacing,
          },
        });
      }
    },
    [addBoardStar, boardId, boardStar, boardStars, memberId, removeBoardStar],
  );

  const showFilledIcon =
    pendingChange === 'starring' || (pendingChange === 'none' && boardStar);

  if (!isMemberLoggedIn() || isClosed) {
    return null;
  }

  return (
    <Tooltip
      content={
        isTemplate ? (
          <FormattedMessage
            id="templates.board_header.click-to-star-or-unstar-this-template-starred-templates-show-up-at-the-top-of-your-boards-list"
            defaultMessage="Click to star or unstar this template. Starred templates show up at the top of your boards list."
            description="Tooltip to star or unstar a board when the board is a template"
          />
        ) : (
          <FormattedMessage
            id="templates.board_header.click-to-star-or-unstar-this-board-starred-boards-show-up-at-the-top-of-your-boards-list"
            defaultMessage="Click to star or unstar this board. Starred boards show up at the top of your boards list."
            description="Tooltip to star or unstar a board"
          />
        )
      }
    >
      <DynamicButton
        className={styles.boardStarButton}
        onClick={
          // We don't want to process clicks if we have a change in progress
          // (e.g. we wouldn't know which id we were removing) or if we're still
          // loading data (we wouldn't know if the board was currently starred
          // or what position we should put a newly starred board at)
          pendingChange === 'none' ? onClick : undefined
        }
        aria-label={intl.formatMessage({
          id: 'templates.board_header.star-or-unstar-board',
          defaultMessage: 'Star or unstar board',
          description: 'Label for the star button',
        })}
        iconBefore={
          showFilledIcon ? (
            headerRedesignEnabled ? (
              <StarStarredIcon
                label={intl.formatMessage({
                  id: 'templates.board_header.star-or-unstar-board',
                  defaultMessage: 'Star or unstar board',
                  description: 'Label for the star button',
                })}
                // @ts-expect-error TS(2322)
                color="var(--ds-background-warning-bold)"
              />
            ) : (
              <StarredIcon
                size="small"
                dangerous_className={classNames(styles.icon, styles.starred)}
              />
            )
          ) : headerRedesignEnabled ? (
            <StarUnstarredIcon
              label={intl.formatMessage({
                id: 'templates.board_header.star-or-unstar-board',
                defaultMessage: 'Star or unstar board',
                description: 'Label for the star button',
              })}
              color="currentColor"
            />
          ) : (
            <StarIcon
              size={headerRedesignEnabled ? 'medium' : 'small'}
              dangerous_className={classNames(styles.icon, styles.unstarred)}
            />
          )
        }
      />
    </Tooltip>
  );
};
